import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { Box, Typography } from "@mui/material";
import {
  CONST_PROJECT_STATUS_TIMELINE_GROUPS,
  CONST_PROJECT_TIMELINE_COLORS,
} from "../../../../constants/lists";
import { ENUM_PROJECT_STATUS_CATEGORY } from "../../../../enums";
import { isSelectedTimeSlotPast } from "../../../../services/date-and-time.utils";
import {
  formatSelectedTimeSlot,
  getStepLabelColor,
  TimelineStepIcon,
} from "./components";
import { getTimelineState } from "./utils";
import { SendMeetingReminderButton } from "../../../../components/buttons";

const TIMELINE_ICON_SIZE = 22;
const TIMELINE_CONNECTOR_OFFSET = TIMELINE_ICON_SIZE / 2 - 1;
const TIMELINE_CONNECTOR_HEIGHT = 18;

function ProjectStatusTimeline({
  status,
  selectedTimeSlot,
  ambassadorInvitationId,
  showSendReminder = false,
}: {
  status?: string;
  selectedTimeSlot?: { from?: string; to?: string };
  ambassadorInvitationId?: string;
  showSendReminder?: boolean;
}) {
  const { activeStepIndex, isClosedStatus, isPastSuccess } =
    getTimelineState(status);
  const showPastSlotTagline =
    isSelectedTimeSlotPast(selectedTimeSlot) &&
    !isPastSuccess &&
    !isClosedStatus;
  const scheduledSlot = formatSelectedTimeSlot(selectedTimeSlot);

  return (
    <Box width="100%">
      <Typography
        fontSize={11}
        fontWeight={600}
        color="#374151"
        mb={1}
        textTransform="uppercase"
        letterSpacing="0.05em"
      >
        Meeting Progress
      </Typography>

      <Box display="flex" flexDirection="column" width="100%" py={0.5}>
        {CONST_PROJECT_STATUS_TIMELINE_GROUPS.map((group, index) => {
          const isCompleted = index < activeStepIndex || isPastSuccess;
          const isActive = index === activeStepIndex && !isPastSuccess;
          const isError = isClosedStatus && isActive;
          const isConnectorCompleted =
            index <= activeStepIndex || isPastSuccess;

          return (
            <Box key={group.category}>
              {index > 0 && (
                <Box
                  width="2px"
                  height={TIMELINE_CONNECTOR_HEIGHT}
                  bgcolor={
                    isConnectorCompleted
                      ? CONST_PROJECT_TIMELINE_COLORS.completed.connector
                      : CONST_PROJECT_TIMELINE_COLORS.pending.border
                  }
                  ml={`${TIMELINE_CONNECTOR_OFFSET}px`}
                />
              )}
              <Box display="flex" alignItems="center" gap={1.25}>
                <TimelineStepIcon
                  category={group.category}
                  isCompleted={isCompleted}
                  isActive={isActive}
                  isError={isError}
                />
                <Box>
                  <Typography
                    fontSize={11}
                    fontWeight={isCompleted || isActive ? 600 : 500}
                    color={getStepLabelColor(isError, isCompleted, isActive)}
                    lineHeight={1.6}
                    whiteSpace="nowrap"
                  >
                    {group.label}
                  </Typography>
                  {group.category === ENUM_PROJECT_STATUS_CATEGORY.UPCOMING &&
                    scheduledSlot && (
                      <Typography
                        fontSize={10}
                        fontWeight={500}
                        color={
                          isActive || isCompleted ? "#2563EB" : "#94A3B8"
                        }
                        lineHeight={1.35}
                        mt={0.375}
                        title={scheduledSlot}
                        noWrap
                      >
                        {scheduledSlot}
                      </Typography>
                    )}
                  {group.category === ENUM_PROJECT_STATUS_CATEGORY.UPCOMING &&
                    showSendReminder &&
                    scheduledSlot && (
                      <SendMeetingReminderButton
                        ambassadorInvitationId={ambassadorInvitationId}
                      />
                    )}
                  {group.category === ENUM_PROJECT_STATUS_CATEGORY.UPCOMING &&
                    showPastSlotTagline && (
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                        mt={0.25}
                      >
                        <WarningAmberOutlinedIcon
                          sx={{ fontSize: 12, color: "#EA580C" }}
                        />
                        <Typography
                          fontSize={10}
                          fontWeight={500}
                          color="#EA580C"
                          lineHeight={1.4}
                        >
                          The scheduled time is no longer valid
                        </Typography>
                      </Box>
                    )}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export { ProjectStatusTimeline };
